import { ComboBox, IComboBoxOption, ISelectableOption, PrimaryButton } from 'office-ui-fabric-react';
import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ILinkDto, IListStore, validateConnection } from "../../../store/integration/common";
import { actionCreators, IP4WConnectionState, IP4WResource, IP4WResourceLinkInfo } from "../../../store/integration/P4WStore";
import OverlayComponent from '../../common/OverlayComponent';
import { usePrevious } from '../../utils/effects';
import { ConnectionField } from '../ConnectionField';
import P4WConnectionSelect from '../P4W/P4WConnectionSelect';
import { buildUserDisplayInfo } from './common';
import { FieldValue } from '../ConnectionFieldValue';

type OwnProps = {
    actions: {
        linkToUser: (linkData: ILinkDto<IP4WResource>) => void;
        deleteUserLink: (connectionId: string) => void;
    };
    sourceData?: IP4WResourceLinkInfo;
    connectionId?: string;
    dismissPanel?: () => void;
}

type StateProps = { 
    resources: IListStore<IP4WResource>;
    connections: IP4WConnectionState;
} 
type Props = StateProps & typeof actionCreators & OwnProps;

const onRenderItem = (props?: ISelectableOption, defaultRender?: (props?: ISelectableOption) => JSX.Element | null): JSX.Element | null => {
    if (!defaultRender) {
        return null;
    }

    const element = defaultRender(props);
    if (!props || !props.disabled) {
        return element;
    }
    return React.createElement("div", { title: "Already linked" }, element);
}

const linkToResource = (props: Props, connectionId?: string, resource?: IP4WResource) => {
    if (!connectionId || !resource) {
        return;
    }

    props.actions.linkToUser({ connectionId, data: resource });
    props.dismissPanel?.();
}

const P4WConnectControl = (props: Props) => {
    const { sourceData, resources, connections } = props;
    const [isLinked, setIsLinked] = React.useState<boolean>(!!props.sourceData);
    const [options, setOptions] = React.useState<IComboBoxOption[]>([]);
    const [selectedResource, setSelectedKey] = React.useState<IP4WResource | undefined>();
    const [connectionId, setConnectionId] = React.useState<string | undefined>(props.connectionId);

    const previousConnectionsVerification = usePrevious(props.connections.connectionsVerification);
    React.useEffect(() => { 
        const selectedConnection = validateConnection(connections, connectionId);
        if (connectionId && previousConnectionsVerification?.[connectionId]?.isValidating !== false
            && selectedConnection?.isValidating === false && !selectedConnection.validationError) {
                props.loadResources(connectionId)
        } 
    }, [connectionId && connections.connectionsVerification[connectionId]]);

    const setResource = (id: string) => {
        const resource = resources.entities.find(_ => _.id === id);
        setSelectedKey(resource);
    }

    React.useMemo(() => {
        const o = resources.entities.map(_ => ({
            key: _.id,
            text: `${_.displayName}${_.email ? ` (${_.email})` : ''}`,
            disabled: _.isAlreadyLinked
        }));
        setOptions(o);
        sourceData && setResource(sourceData.id);
    }, [resources.entities, sourceData]);
    
    React.useMemo(() => { setIsLinked(!!sourceData) }, [!!sourceData]);

    const getComboboxValidationMessage = React.useCallback(() => {
        if (isLinked || !connectionId || props.resources.isLoading) {
            return undefined;
        }

        return resources.error 
            ? resources.error
            : selectedResource
                ? selectedResource.isAlreadyLinked
                    ? "Resource already linked"
                    : undefined
                : `Please select resource from list`;
    }, []);

    const _onConnectionChange = (_?: string) => {
        if (isLinked) {
            return;
        }
        setConnectionId(_);
        setSelectedKey(undefined);
    }

    const deleteResourceLink = () => {
        if (!connectionId) {
            return;
        }
    
        props.actions.deleteUserLink(connectionId);
        setSelectedKey(undefined);
    }

    const setSelectedResource = (key?: string) => {
        if(!key) {
            setSelectedKey(undefined);
            return;
        }
        setResource(key);
    }

    const resourceDisplayInfo = selectedResource?.displayName && selectedResource?.email && buildUserDisplayInfo(selectedResource.displayName, selectedResource.email)
        || sourceData && buildUserDisplayInfo(sourceData.displayName, sourceData.email);

    const isValid = validateConnection(props.connections, connectionId);

    return <>
        <div className="with-top-margin">
            <P4WConnectionSelect
                disabled={isLinked}
                connectionId={connectionId}
                onChange={_onConnectionChange} />
        </div>
        <div className="with-top-margin">
            <OverlayComponent isOverlayed={!connectionId || isValid?.isValidating !== false || !!isValid?.validationError}>
                <ConnectionField 
                    label="Resource"
                    isLinked={isLinked}
                    isLoading={resources.isLoading}
                    errorMessage={resources.error}
                    editor={ <ComboBox 
                        disabled={isLinked || !connectionId || resources.isLoading}
                        placeholder="Select resource"
                        selectedKey={selectedResource ? selectedResource.id : undefined}
                        useComboBoxAsMenuWidth
                        allowFreeform
                        options={options}
                        onRenderItem={onRenderItem}
                        onChange={(_, __) => setSelectedResource(__?.key as string | undefined)}
                        errorMessage={isValid?.isValidating === false && !isValid?.validationError ? getComboboxValidationMessage() : undefined}
                    />}
                    viewer={ <FieldValue value={resourceDisplayInfo} /> }
                />
                {
                    isLinked
                        ? <PrimaryButton text="Delete resource link"
                            className="with-top-margin"
                            onClick={deleteResourceLink} />
                        : <PrimaryButton text="Link resource"
                            disabled={!selectedResource}
                            className="with-top-margin"
                            onClick={() => linkToResource(props, connectionId, selectedResource)} />
                }
            </OverlayComponent>
        </div>
    </>
}

function mapStateToProps(state: ApplicationState): StateProps {
    return { 
        resources: state.p4w.resources,
        connections: state.p4w.connections
    };
}

export default connect(mapStateToProps, actionCreators)(P4WConnectControl);